import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Form } from "react-bootstrap";
import { PageHeader } from "../../components/dashboard/DashboardUI";
import Loader from "../../components/loader/Loader";
import {
  deleteAccount,
  removeAuthedUser,
  updateAccount,
} from "../../redux/actions/AuthUserActions";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import ReactFlagsSelect from "react-flags-select";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";

countries.registerLocale(fr);

function UserProfile() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const [showModal, setShowModal] = useState({
    info: false,
    email: false,
    password: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [countrySelected, setCountrySelected] = useState(user.country);

  const handleShowModal = (type) =>
    setShowModal({ ...showModal, [type]: true });
  const handleCloseModal = () =>
    setShowModal({ info: false, email: false, password: false });

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });

    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      handleCloseModal();
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }

    setSubmitting(false);
  };

  // Formik validation schema for password change
  const passwordValidationSchema = Yup.object({
    newPassword: Yup.string()
      .min(6, "Le mot de passe doit contenir au moins 6 caractères")
      .required("Le mot de passe est obligatoire"),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("newPassword"), null],
        "Les mots de passe ne correspondent pas",
      )
      .required("La confirmation du mot de passe est obligatoire"),
  });

  // Formik validation schema for email change
  const emailValidationSchema = Yup.object({
    email: Yup.string().email("Email invalide").required("Email requis"),
  });

  // Formik hook info modal
  const formikInfo = useFormik({
    initialValues: {
      firstname: user.firstname,
      lastname: user.lastname,
      country: user.country,
      mobile: user?.mobile,
      whatsApp: user?.whatsApp || "",
      address: user?.address || "",
      profession: user?.profession || "",
    },
    onSubmit: (values) => {
      Swal.fire({
        title: "Êtes-vous sûr ?",
        text: "Voulez-vous vraiment changer vos informations ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, confirmer",
        cancelButtonText: "Annuler",
      }).then((result) => {
        if (result.isConfirmed) {
          setSubmitting(true);
          dispatch(
            updateAccount(
              user.token,
              user._id,
              {
                ...values,
              },
              handleCallback,
            ),
          );
        }
      });
    },
  });

  // Formik hook password modal
  const formikEmail = useFormik({
    initialValues: {
      email: user.email,
    },
    validationSchema: emailValidationSchema,
    onSubmit: (values) => {
      Swal.fire({
        title: "Êtes-vous sûr ?",
        text: "Voulez-vous vraiment changer votre adresse mail ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, confirmer",
        cancelButtonText: "Annuler",
      }).then((result) => {
        if (result.isConfirmed) {
          setSubmitting(true);
          dispatch(
            updateAccount(
              user.token,
              user._id,
              { email: values.email },
              handleCallback,
            ),
          );
        }
      });
    },
  });

  // Formik hook password modal
  const formikPassword = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values) => {
      Swal.fire({
        title: "Êtes-vous sûr ?",
        text: "Voulez-vous vraiment changer de mot de passe ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, confirmer",
        cancelButtonText: "Annuler",
      }).then((result) => {
        if (result.isConfirmed) {
          setSubmitting(true);
          dispatch(
            updateAccount(
              user.token,
              user._id,
              { password: values.newPassword },
              handleCallback,
            ),
          );
        }
      });
    },
  });

  const handleDeleteAccount = () => {
    Swal.fire({
      title: "Supprimer le compte",
      text: "Cette action est irréversible. Voulez-vous vraiment continuer ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed) {
        setSubmitting(true);
        dispatch(deleteAccount(user.token, user._id, handleDeleteCallback));
      }
    });
  };

  const handleDeleteCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });

    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      dispatch(removeAuthedUser());
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }

    setSubmitting(false);
  };

  const profileRows = [
    ["Nom", `${user.firstname} ${user.lastname}`],
    ["Téléphone", user?.mobile || "Non renseigné"],
    ["WhatsApp", user.whatsApp || "Non renseigné"],
    ["Pays", countries.getName(user.country, "fr") || user.country],
    ["Adresse", user.address || "Non renseignée"],
    ["Profession", user.profession || "Non renseignée"],
  ];

  return (
    <div className="dash-profile">
      <Loader isLoading={submitting} />

      <PageHeader
        title="Mon profil"
        lead="Vos informations personnelles et votre adresse de connexion."
      />

      <section className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Informations personnelles</h2>
          <button
            type="button"
            className="dash-link-button"
            onClick={() => handleShowModal("info")}
          >
            Modifier
          </button>
        </div>
        <dl className="dash-details">
          {profileRows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      <section className="dash-card dash-section">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Adresse e-mail</h2>
          <button
            type="button"
            className="dash-link-button"
            onClick={() => handleShowModal("email")}
          >
            Modifier
          </button>
        </div>
        <p className="dash-page-lead">
          {user.email} · utilisée pour recevoir vos codes de connexion.
        </p>
      </section>

      <section className="dash-card dash-section dash-danger-zone">
        <div>
          <h2 className="dash-card-title">Supprimer mon compte</h2>
          <p className="dash-page-lead">
            Cette action est définitive et supprime l'accès à votre espace.
          </p>
        </div>
        <button
          type="button"
          className="dash-button is-danger"
          onClick={handleDeleteAccount}
        >
          Supprimer mon compte
        </button>
      </section>

      {/* Modal pour modifier les informations */}
      <Modal show={showModal.info} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Modifier les informations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={formikInfo.handleSubmit}
            style={{ textAlign: "left" }}
          >
            {/* Nom et Prénom sur la même ligne */}
            <div className="row mb-3 g-3">
              <div className="col-md-6">
                <Form.Group controlId="lastname">
                  <Form.Label>Nom</Form.Label>
                  <Form.Control
                    name="lastname"
                    placeholder="Nom"
                    onChange={formikInfo.handleChange}
                    onBlur={formikInfo.handleBlur}
                    value={formikInfo.values.lastname}
                    isInvalid={
                      formikInfo.touched.lastname && formikInfo.errors.lastname
                    }
                    style={{
                      height: "45px",
                      borderRadius: "5px",
                      borderBlockColor: "#c4c4c4",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikInfo.errors.lastname}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="firstname">
                  <Form.Label>Prénom</Form.Label>
                  <Form.Control
                    name="firstname"
                    placeholder="Prénom"
                    onChange={formikInfo.handleChange}
                    onBlur={formikInfo.handleBlur}
                    value={formikInfo.values.firstname}
                    isInvalid={
                      formikInfo.touched.firstname &&
                      formikInfo.errors.firstname
                    }
                    style={{
                      height: "45px",
                      borderRadius: "5px",
                      borderBlockColor: "#c4c4c4",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikInfo.errors.firstname}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* numéros*/}
            <div className="row mb-3 g-3">
              <div className="col-md-6">
                <Form.Group controlId="mobile">
                  <Form.Label>Numéro de téléphone</Form.Label>
                  <PhoneInput
                    country={formikInfo.values.country.toLowerCase() || ""}
                    value={formikInfo.values.mobile || ""}
                    placeholder="Numéro de Téléphone"
                    onChange={(phone) => {
                      const formattedPhone = phone.startsWith("+")
                        ? phone
                        : `+${phone}`;
                      formikInfo.setFieldValue("mobile", formattedPhone);
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "45px",
                      fontSize: "14px",
                    }}
                  />
                  {formikInfo.errors.mobile && formikInfo.touched.mobile && (
                    <div
                      className="text-danger"
                      style={{ textAlign: "left", fontSize: "14px" }}
                    >
                      {formikInfo.errors.mobile}
                    </div>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group controlId="whatsApp">
                  <Form.Label>Numéro WhatsApp</Form.Label>
                  <PhoneInput
                    country={formikInfo.values.country.toLowerCase() || ""}
                    value={formikInfo.values.whatsApp || ""}
                    placeholder="Numéro WhatsApp"
                    onChange={(phone) => {
                      const formattedPhone = phone.startsWith("+")
                        ? phone
                        : `+${phone}`;
                      formikInfo.setFieldValue("whatsApp", formattedPhone);
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "45px",
                      fontSize: "14px",
                    }}
                  />
                  {formikInfo.errors.whatsApp &&
                    formikInfo.touched.whatsApp && (
                      <div
                        className="text-danger"
                        style={{ textAlign: "left", fontSize: "14px" }}
                      >
                        {formikInfo.errors.whatsApp}
                      </div>
                    )}
                </Form.Group>
              </div>
            </div>

            {/* Pays et adresse*/}
            <div className="row mb-3 g-3">
              <div className="col-md-6">
                <Form.Group controlId="country">
                  <Form.Label>Pays de residence</Form.Label>
                  <ReactFlagsSelect
                    selected={countrySelected}
                    onSelect={(code) => {
                      formikInfo.setFieldValue("country", code);
                      setCountrySelected(code);
                    }}
                    placeholder="Pays de résidence"
                    searchable
                    searchPlaceholder="Recherchez votre pays"
                    selectButtonClassName="w-100 custom-select"
                    className="custom-select"
                  />
                  {formikInfo.errors.country && formikInfo.touched.country && (
                    <div
                      className="text-danger"
                      style={{ textAlign: "left", fontSize: "14px" }}
                    >
                      {formikInfo.errors.country}
                    </div>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group controlId="address">
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control
                    name="address"
                    placeholder="Adresse"
                    onChange={formikInfo.handleChange}
                    onBlur={formikInfo.handleBlur}
                    value={formikInfo.values.address}
                    isInvalid={
                      formikInfo.touched.address && formikInfo.errors.address
                    }
                    style={{
                      height: "45px",
                      borderRadius: "5px",
                      borderBlockColor: "#c4c4c4",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikInfo.errors.address}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="profession">
              <Form.Label>Profession</Form.Label>
              <Form.Control
                name="profession"
                placeholder="Profession"
                onChange={formikInfo.handleChange}
                onBlur={formikInfo.handleBlur}
                value={formikInfo.values.profession}
                isInvalid={
                  formikInfo.touched.profession && formikInfo.errors.profession
                }
                style={{
                  height: "45px",
                  borderRadius: "5px",
                  borderBlockColor: "#c4c4c4",
                }}
              />
              <Form.Control.Feedback type="invalid">
                {formikInfo.errors.profession}
              </Form.Control.Feedback>
            </Form.Group>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal pour modifier l'email */}
      <Modal show={showModal.email} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Modifier l'email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formikEmail.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nouvel email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formikEmail.values.email}
                onChange={formikEmail.handleChange}
                onBlur={formikEmail.handleBlur}
                isInvalid={
                  formikEmail.touched.email && formikEmail.errors.email
                }
              />
              <Form.Control.Feedback type="invalid">
                {formikEmail.errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal pour modifier le mot de passe */}
      <Modal show={showModal.password} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Modifier le mot de passe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formikPassword.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={formikPassword.values.newPassword}
                onChange={formikPassword.handleChange}
                onBlur={formikPassword.handleBlur}
                isInvalid={
                  formikPassword.touched.newPassword &&
                  formikPassword.errors.newPassword
                }
              />
              <Form.Control.Feedback type="invalid">
                {formikPassword.errors.newPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirmer le mot de passe</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formikPassword.values.confirmPassword}
                onChange={formikPassword.handleChange}
                onBlur={formikPassword.handleBlur}
                isInvalid={
                  formikPassword.touched.confirmPassword &&
                  formikPassword.errors.confirmPassword
                }
              />
              <Form.Control.Feedback type="invalid">
                {formikPassword.errors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UserProfile;
